import { Suspense, useMemo } from "react";
import {
  createFileRoute,
  isRedirect,
  redirect,
  useNavigate,
  useSearch,
  useLocation,
} from "@tanstack/react-router";
import { PayoutsFilterParams } from "./payouts/-components/use-payouts";
import { PayoutsFilters } from "./payouts/-components/payouts-filters";
import { PayoutsTable } from "./payouts/-components/payouts-table";
import { ExchangeTableLoadingFallback } from "./-components/exchange-table-loading-fallback";
import { z } from "zod/v4";
import { zodValidator } from "@tanstack/zod-adapter";
import { CardWithHeader } from "@/components/card-with-header";
import { applicationConfigQuery } from "@/lib/queries";
import { ApplicationConfigNotFoundError } from "@/lib/application-api";

const searchParamsSchema = z.object({
  expandedRow: z.string().optional(),
  payoutId: z.string().optional(),
  statuses: z.string().optional(),
  minAmount: z.number().optional(),
  maxAmount: z.number().optional(),
  startDateUtc: z.union([z.string(), z.number()]).optional(),
  endDateUtc: z.union([z.string(), z.number()]).optional(),
  clientReference: z.string().optional(),
});

function toValidDateTimestamp(value: number | string | undefined) {
  if (value === undefined || value === "") return undefined;

  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.getTime() : undefined;
}

export const Route = createFileRoute(
  "/(app)/_main-layout/_exchange/exchange/payouts",
)({
  component: PayoutsPageComponent,
  validateSearch: zodValidator(searchParamsSchema),
  beforeLoad: async ({ context }) => {
    // Guard: redirect to /exchange if FX is not enabled.
    try {
      const config = await context.queryClient.ensureQueryData(
        applicationConfigQuery(
          context.selectedApplicationId,
          context.accessToken,
        ),
      );
      if (!config.enabled) {
        throw redirect({ to: "/exchange" });
      }
    } catch (error) {
      if (isRedirect(error)) throw error;
      if (error instanceof ApplicationConfigNotFoundError) {
        throw redirect({ to: "/exchange" });
      }
      // Non-config errors (network, etc.) — let the component handle them
    }
  },
});

function PayoutsPageComponent() {
  const search = useSearch({
    from: "/(app)/_main-layout/_exchange/exchange/payouts",
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Extract filter params from search
  const filters: PayoutsFilterParams | undefined = useMemo(() => {
    const startDateUtc = toValidDateTimestamp(search.startDateUtc);
    const endDateUtc = toValidDateTimestamp(search.endDateUtc);
    const hasFilters =
      search.payoutId ||
      search.statuses ||
      search.minAmount !== undefined ||
      search.maxAmount !== undefined ||
      startDateUtc !== undefined ||
      endDateUtc !== undefined ||
      search.clientReference;

    if (!hasFilters) return undefined;

    return {
      payoutId: search.payoutId,
      statuses: search.statuses,
      minAmount: search.minAmount,
      maxAmount: search.maxAmount,
      startDateUtc,
      endDateUtc,
      clientReference: search.clientReference,
    };
  }, [
    search.payoutId,
    search.statuses,
    search.minAmount,
    search.maxAmount,
    search.startDateUtc,
    search.endDateUtc,
    search.clientReference,
  ]);

  // Toggle row expansion
  const toggleRow = (payoutId: string) => {
    const currentExpandedRow = search.expandedRow;
    if (currentExpandedRow === payoutId) {
      // Close: remove the query parameter
      navigate({
        to: location.pathname,
        search: (currentParams) => ({
          ...currentParams,
          expandedRow: undefined,
        }),
      });
    } else {
      // Expand: set the query parameter
      navigate({
        to: location.pathname,
        search: (currentParams) => ({
          ...currentParams,
          expandedRow: payoutId,
        }),
      });
    }
  };

  return (
    <div className="space-y-6">
      <CardWithHeader
        variant="exchange"
        label="Payout History"
        title="Search and filter your exchange payouts"
      >
        <Suspense
          fallback={
            <ExchangeTableLoadingFallback
              headers={[
                "",
                "Payout ID",
                "Amount",
                "Status",
                "Reference",
                "Created At",
              ]}
              message="Loading payouts..."
            />
          }
        >
          <PayoutsTable
            filters={filters}
            expandedRow={search.expandedRow}
            onRowToggle={toggleRow}
            renderControls={(rightControl) => (
              <div className="mb-4">
                <PayoutsFilters rightControl={rightControl} />
              </div>
            )}
          />
        </Suspense>
      </CardWithHeader>
    </div>
  );
}
